import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import React, { ReactElement, useRef } from "react";
import { HiOutlineSparkles } from "react-icons/hi2";

import { CopyableChart } from "../../../../components/ai-elements";
import { useCustomInsightsChat } from "./CustomInsightsChatProvider";
import { CustomInsightsReportPrettyMarkdown } from "./CustomInsightsReportPrettyMarkdown";
import CustomInsightsResultCard from "./CustomInsightsResultCard";
import { AssistantTextBubbleRaw } from "./InsightsChatMessages";
import { ResultActions } from "./ResultActions";

export function InsightsWelcomeContent(): ReactElement {
  const { rerunReport } = useCustomInsightsChat();

  return (
    <Flex flexDir="column" pr="16px">
      {rerunReport ? (
        <AssistantTextBubbleRaw
          text={`I've loaded the results and filters from **${rerunReport.title}**. You can continue your exploration, update the filters, and re-run this report. The prompt used to generate this report:\n\n*${rerunReport.prompt}*`}
        />
      ) : (
        <Flex
          gap="8px"
          alignItems="flex-start"
          pb="12px"
          pl="8px"
          pr="12px"
          pt="8px"
          borderRadius="12px"
          w="100%"
        >
          <Flex
            alignItems="center"
            justifyContent="center"
            borderRadius="100px"
            flexShrink={0}
            w="24px"
            h="24px"
          >
            <Icon as={HiOutlineSparkles} boxSize="24px" color="accent.600" />
          </Flex>
          <Box pt="2px" flex={1} minW={0}>
            <Text
              fontSize="16px"
              lineHeight="24px"
              fontWeight="normal"
              color="gray.800"
            >
              Turn your interview data into insights. Ask me to identify trends,
              compare cohorts, analyze interviewer signal, or dig into
              what&apos;s driving hiring outcomes. Start with a suggestion or
              ask your own question.
            </Text>
          </Box>
        </Flex>
      )}
    </Flex>
  );
}

export function RerunReportResults(): ReactElement | null {
  const { rerunReport } = useCustomInsightsChat();
  const contentRef = useRef<HTMLDivElement>(null);

  if (!rerunReport?.id) return null;
  const { charts, summary } = rerunReport;
  const hasContent = (charts?.length ?? 0) > 0 || !!summary;
  if (!hasContent) return null;

  return (
    <CustomInsightsResultCard
      bodyRef={contentRef}
      footer={
        <ResultActions reportId={rerunReport.id} contentRef={contentRef} />
      }
    >
      <Box>
        {charts?.map((chartData, index) => (
          <Box
            key={chartData.toolCallId || index}
            mb={index === charts.length - 1 ? 0 : 6}
          >
            <CopyableChart
              variant="insightsReport"
              data={{
                title: chartData.title,
                type: chartData.chart_type ?? "bar",
                labels: chartData.labels,
                values: chartData.values,
                series: chartData.series,
              }}
            />
          </Box>
        ))}
        {summary && (
          <Box width="100%" data-copy-role="text-bubble">
            <Box flex={1} minW={0} overflowX="auto">
              <CustomInsightsReportPrettyMarkdown>
                {summary}
              </CustomInsightsReportPrettyMarkdown>
            </Box>
          </Box>
        )}
      </Box>
    </CustomInsightsResultCard>
  );
}
